<script setup lang="ts">
import {computed, nextTick, onMounted, onUnmounted, ref, shallowRef, ShallowRef, toValue, watch} from "vue";
import Translator from "@/utils/common/Translator.ts";
import EditScreenGridHandler from "@/utils/common/EditScreenGridHandler.ts";
import {
  CellDoubleClickedEvent,
  CellEditingStartedEvent,
  CellEditingStoppedEvent,
  CellFocusedEvent,
  CellKeyDownEvent,
  FullWidthCellKeyDownEvent,
  GetRowIdFunc,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  ProcessDataFromClipboardParams,
  RowClassParams,
  RowDragEndEvent,
  RowDragEnterEvent,
  RowSelectionOptions,
  SelectionColumnDef,
  SideBarDef,
  StateUpdatedEvent
} from "ag-grid-enterprise";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import {AgGridVue} from "ag-grid-vue3";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import {BlockProps} from "@/utils/presenters/interfaces.ts";
import NaMaConfirmationDialog from "@/utils/forms/NaMaConfirmationDialog.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import StringConstants from "@/utils/constants/StringConstants.ts";
import DialogHandler, {CommonDialogAction} from "@/utils/forms/DialogHandler.ts";
import AgShowFieldInfoContextMenu from "@/utils/common/AgShowFieldInfoContextMenu.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import FieldToFocusUtils from "@/utils/common/FieldToFocusUtils.ts";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {useMessagesStore} from "@/stores/ToastDataStore.ts";
import {ColDef, ColGroupDef, GridState, SuppressHeaderKeyboardEventParams, SuppressKeyboardEventParams} from "ag-grid-community";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";
import BlockUtils from "@/utils/common/BlockUtils.ts";
import {onClickOutside} from "@vueuse/core";
import {useNamaBreakpoints} from "@/composables/useNamaBreakpoints.ts";
import {attachInputRestrictionsHandler} from "@/composables/useInputRestrictions.ts";
import EditScreenGrid = namaTypes.EditScreenGrid;
import QuestionField = namaTypes.QuestionField;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;

const emit = defineEmits<{ (e: 'toggleFindBar'): void; }>();
const props = defineProps<BlockProps>();
const blockRef = computed<EditScreenGrid>(() => props.block as EditScreenGrid);

const messagesStore = useMessagesStore();
const presenter = props.presenter!;
const dispatcher = presenter.dispatcher!;
const gridId = blockRef.value.id!;
props.presenter.addGridToGridEventBus(gridId);
const handler = new EditScreenGridHandler(blockRef.value, presenter);
presenter.registerGridHandler(handler);
const gridApi: ShallowRef<GridApi | undefined> = shallowRef();
const gridStateKey = 'grid-state_' + presenter.entityType + '_' + gridId;
const gridElement: ShallowRef<typeof AgGridVue | undefined> = shallowRef();
const rowSelection: RowSelectionOptions = {mode: "multiRow", checkboxes: true, headerCheckbox: true}
const rowData = ref<any[] | undefined>(undefined);
const suppressColumnDrag = ref(AGGridUtils.getSuppressColumnDragFromStorage(gridId));
const findSearchValue = ref(undefined);
AGGridUtils.watchRowNumberOptions(gridApi);

const selectionColumnDef: SelectionColumnDef = {
  resizable: true,
  maxWidth: 90,
  suppressKeyboardEvent: (params: SuppressKeyboardEventParams): boolean => {
    return params.event.ctrlKey && params.event.key === "Delete";
  }, suppressHeaderKeyboardEvent: (params: SuppressHeaderKeyboardEventParams): boolean => {
    return params.event.ctrlKey && params.event.key === "Delete";
  },
  rowDrag: (params: any) => {
    return !params.data.masterRowId;
  },
}
const gridVisible = ref(false);
const initialState = ref<GridState | undefined>(undefined);
const hasStoredState = ref(false);
const gridColIdsKey = gridStateKey + AGGridUtils.COL_IDS_SUFFIX;

function removeDefaultColDefWidthIfNeeded() {
  if (!initialState.value)
    return;
  const colDefs = toValue(columnDefs) ?? []
  const columnSizing = initialState.value.columnSizing?.columnSizingModel ?? []
  for (const column of columnSizing) {
    const initialWidth = column.width;
    const colId = column.colId;
    let find = AGGridUtils.findColDef(colDefs, colId);
    if (!find)
      continue;
    const isChanged = find.width !== initialWidth;
    if (isChanged)
      find.width = undefined;
  }
}

const columnDefs = ref<ColDef[]>(handler.fetchColDefs())
const isGridReady = ref(false)
const onGridReady = async (params: GridReadyEvent) => {
  gridApi.value = params.api;
  refreshBooleanColumnsIfNeeded();
  gridApi.value.setGridOption(AGGridUtils.suppressRowDragKey, AGGridUtils.getSuppressRowDragFromStorage(gridId));
  gridApi.value.setGridOption(AGGridUtils.suppressMovableColumnsKey, AGGridUtils.getSuppressColumnDragFromStorage(gridId));
  const colDefs: (ColGroupDef | ColDef)[] | undefined = toValue(columnDefs)
  if (colDefs) {
    if (useNamaBreakpoints().isLaptop.value) {
      colDefs.forEach(cf => {
        if ('children' in cf && cf.children)
          cf.children.forEach(normalizeColDefWidth);
        else {
          normalizeColDefWidth(cf);
        }
      });
    }
  }

  isGridReady.value = true;

  const showTotalRowCount = namaFlagsStore.getGridOption('showTotalRowCount');
  if (showTotalRowCount === false) {
    gridApi.value.setGridOption('statusBar', {statusPanels: [{statusPanel: "agAggregationComponent"}]});
  }
  const enableCellTextSelection = namaFlagsStore.getGridOption('enableCellTextSelection');
  if (ObjectChecker.isNotNullOrUndefined(enableCellTextSelection)) {
    gridApi.value.setGridOption('enableCellTextSelection', enableCellTextSelection);
  }
  handler.addGridApi(toValue(gridApi))
};

const onStateUpdated = (event: StateUpdatedEvent) => {
  NamaCommon.setInStorage(gridStateKey, event.state);
  const knownColIds = gridApi.value?.getColumnState().map(state => state.colId);
  if (knownColIds)
    NamaCommon.setInStorage(gridColIdsKey, knownColIds);
};

const namaFlagsStore = useNamaFlagsStore();

const agShowFieldInfoContextMenu = new AgShowFieldInfoContextMenu()
const {fieldToFocusOptions, gridOptions} = storeToRefs(useNamaFlagsStore());
const sideBarDef: SideBarDef = {
  defaultToolPanel: undefined,
  toolPanels: [
    {...AGGridUtils.getColumnsToolPanel()},
    {...AGGridUtils.getFilterToolPanel()},
    {...AGGridUtils.getSettingsToolPanel()}
  ],
}

watch(gridOptions, () => {
  if (!gridApi.value)
    return;
  gridApi.value?.setGridOption("enableCellTextSelection", namaFlagsStore.getGridOption("enableCellTextSelection"))
  const showTotalRowCount = namaFlagsStore.getGridOption("showTotalRowCount");
  const panels = showTotalRowCount
    ? [{statusPanel: "agAggregationComponent"}, {statusPanel: "agTotalRowCountComponent"}]
    : [{statusPanel: "agAggregationComponent"}];
  gridApi.value.setGridOption('statusBar', {statusPanels: panels});
}, {deep: true})

function refreshBooleanColumnsIfNeeded() {
  const booleanColumns = toValue(columnDefs)
    .filter(colDef => colDef.context?.fieldType === FieldType.Boolean)
    .map(colDef => colDef.field)
    .filter(field => field !== undefined);
  if (booleanColumns.length > 0) {
    gridApi.value?.setGridOption("rowData", rowData.value);
    gridApi.value?.refreshCells({columns: booleanColumns});
  }
}

async function updateData() {
  const record = presenter.fetchRecord();
  if (ObjectChecker.isNullOrUndefined(record))
    return;
  let rows = NamaCommon.getFieldValue(record, gridId);
  //todo: Qasid commented that areDeepEqual cost might be too high
  if (ObjectChecker.isNotEmptyOrNull(rows) && ObjectChecker.areDeepEqual(rows, rowData.value))
    return;
  const isGridDisabled = presenter.isFieldDisabled(gridId);
  if (ObjectChecker.isNullOrUndefined(rows) || !Array.isArray(rows)) {
    if (isGridDisabled) {
      gridApi.value?.setGridOption("rowData", []);
    } else {
      NamaCommon.setFieldValue(record, gridId, []);
      rows = NamaCommon.getFieldValue(presenter.fetchRecord(), gridId);
    }
  }
  if (rows && rows.length < 1 && !isGridDisabled) {
    rows.push({
      id: await ClientCache.asyncNextGUIDId()
    });
    await dispatcher.prevalidateAllFieldsOfGridAtLine(handler.listCurrentlyVisibleColumns(), gridId, 0);
  }
  AGGridUtils.syncAgGridRows(presenter, gridId, gridApi);
  if (presenter.fetchGridSelectedLine(gridId) >= (rows?.length ?? 0))
  presenter.updateGridSelectedLine(gridId, 0);
}

const isUpdating = ref(false)
watch(() => isGridReady.value && NamaCommon.getFieldValue(presenter.fetchRecord(), gridId), async () => {
  if (!isGridReady.value || presenter.isBulkUpdating || isUpdating.value)
    return;
  isUpdating.value = true
  await updateData()
  isUpdating.value = false
}, {deep: true})

onClickOutside(gridElement, (evt) => {
  const focusedCell = gridApi.value?.getFocusedCell();
  const target = evt.target as Node;
  if (gridElement.value?.$el?.contains(target))
    return;
  let fieldType: string | undefined = focusedCell?.column?.getColDef()?.context?.fieldType;
  if (focusedCell && focusedCell.column?.getColId() != "ag-Grid-SelectionColumn" && ObjectChecker.isAnyEqualToFirst(fieldType, FieldType.Decimal, FieldType.Integer)) {
    gridApi.value?.stopEditing();
    gridApi.value?.clearFocusedCell();
  }
})

handler.focusHandler = () => {
  gridElement.value!.$el.focus();
}

function normalizeColDefWidth(colDef: ColDef) {
  if (colDef?.context?.fieldType !== FieldType.BigText || ObjectChecker.isAnyEqualToFirst(colDef?.context?.columnMetaData, IdsAndConstants.details_specificDimensions_serialNumber,
    IdsAndConstants.details_specificDimensions_secondSerial))
    return;
  const divsPadding = 88;
  colDef.maxWidth = (window.innerWidth - divsPadding) * 0.3;
}

function onEnterKey(e: KeyboardEvent) {
  AGGridUtils.navigateToNextCellAndStartEditing(gridApi.value!);
}

let lastStartEditEvent: CellEditingStartedEvent | null = null;
let mySetterCB = (columnId: string, row: number, newValue: any) => {
  if (!lastStartEditEvent)
    return;
  let decodeColId = AGGridUtils.decodeColId(lastStartEditEvent.column.getColId());
  const blockIdWithDot = blockRef.value.fieldId + ".";
  if (!decodeColId.startsWith(blockIdWithDot))
    decodeColId = blockIdWithDot + decodeColId;
  if ((columnId === decodeColId || decodeColId.startsWith(columnId + ".")) && row === lastStartEditEvent.rowIndex) {
    let cellEditorInstances = lastStartEditEvent.api.getCellEditorInstances();
    if (!cellEditorInstances || !cellEditorInstances.length)
      return;
    let editor = cellEditorInstances[0] as any;
    if (columnId === blockRef.value.fieldId && Array.isArray(newValue) && newValue.length > lastStartEditEvent.rowIndex!)//update is for grid itself
    {
      decodeColId = decodeColId.includes(columnId) ? decodeColId.replaceAll(columnId + '.', '') : decodeColId;
      if (editor.$root && editor.$root.customFetchValueFromRow)
        newValue = editor.$root.customFetchValueFromRow(newValue[lastStartEditEvent.rowIndex!], decodeColId);
      else
        newValue = NamaCommon.getFieldValue(newValue[lastStartEditEvent.rowIndex!], decodeColId);
    }
    if (editor.$root && editor.$root.namaUpdateValue) {
      editor.$root.namaUpdateValue(newValue);
    } else if (editor.eInput && editor.eInput.setValue)
      editor.eInput.setValue(newValue);
    else
      editor.cellEditorInput?.eEditor?.setValue(newValue)
  }
};

let inputRestrictionsCleanup: (() => void) | null = null;

function onEditingStopped(event: CellEditingStoppedEvent) {
  if (FieldWidgetUtil.columnSetterCB === mySetterCB)
    FieldWidgetUtil.columnSetterCB = null;
  lastStartEditEvent = null;
  inputRestrictionsCleanup?.();
  inputRestrictionsCleanup = null;
}

function onEditingStarted(event: CellEditingStartedEvent) {
  FieldWidgetUtil.columnSetterCB = mySetterCB;
  lastStartEditEvent = event;
  let cellEditorInstances = event.api.getCellEditorInstances();
  if (!cellEditorInstances || !cellEditorInstances.length)
    return;
  let editor = cellEditorInstances[0] as any;
  if (editor.eInput) {
    (editor.eInput.eInput as HTMLInputElement).setAttribute("enterkeyhint", "go");
    const colId = event.column?.getColId();
    const fieldId = colId ? AGGridUtils.decodeColId(colId) : '';
    const fieldStyle = ClientCache.getFieldStyle(presenter.entityType, fieldId);
    if (fieldStyle?.fixedInputLanguage || fieldStyle?.fixedLetterCase)
      inputRestrictionsCleanup = attachInputRestrictionsHandler(editor.eInput.eInput as HTMLElement, fieldStyle.fixedInputLanguage, fieldStyle.fixedLetterCase);
  }
}

const getRowId = ref<GetRowIdFunc>((params: GetRowIdParams) => String(params.data.id))

async function duplicateMultipleRowsDialog(currentRowData: Record<string, any>, api: GridApi, rowIndex: number) {
  if (AGGridUtils.isEmptyLine(currentRowData)) {
    messagesStore.pushMessageStr("Could not duplicate empty line");
    return;
  }
  let okBtn: CommonDialogAction = CommonDialogAction.createOkDialogAction(async (record: Record<string, any>) => {
    let count = record['count'];
    await AGGridUtils.asyncCreateRows(currentRowData, api, rowIndex, true, presenter, gridId, count)
  });
  let cancelBtn: CommonDialogAction = CommonDialogAction.createCancelDialogAction(() => {
  });
  let questionField: QuestionField = NamaCommon.createQuestionField("count", FieldType.Integer);
  let defaultValue: DTOFilledQuestionField = {id: 'count', value: '1', objValue: 1};
  let dialogBox = new DialogHandler([okBtn, cancelBtn], Translator.translateId("count"), true);
  await dialogBox.createPresenter(presenter, [questionField], [defaultValue]);
  await dialogBox.runDialogBox();
}

async function onCellKeyDown(event: CellKeyDownEvent | FullWidthCellKeyDownEvent) {
  let keyboardEvent = event.event! as KeyboardEvent;
  let api = event.api;
  let currentRowData = event.data as Record<string, any>;
  let rowId = currentRowData.id;
  let rowIndex = event.rowIndex!;
  let rows: any[] = NamaCommon.getFieldValue(presenter.record.value, gridId);
  let realRowIndex = ObjectChecker.findIndexOf(rows, l => l.id === rowId)!;
  let focusedCell = api.getFocusedCell();
  if (keyboardEvent.key === 'Insert') {
    let shouldPerformGridAction = false;
    if (!AGGridUtils.canAppendRow(presenter, gridId))
      return;
    if (keyboardEvent.ctrlKey) {
      if (keyboardEvent.altKey) {
        if (!AGGridUtils.canCopyRow(dispatcher, gridId))
          return;
        await duplicateMultipleRowsDialog(currentRowData, api, realRowIndex);
        shouldPerformGridAction = true;
      } else {
        if (AGGridUtils.isEmptyLine(currentRowData)) {
          messagesStore.pushMessageStr("Could not insert new line while you have an empty line");
          return;
        }
        if (!api.isAnyFilterPresent() || await NaMaConfirmationDialog.okCancelConfirmationDialog(
          Translator.translateId('areYouSureToAddLinesThatMayNotBeDisplayed'), true)) {
          await AGGridUtils.asyncCreateRows(currentRowData, api, realRowIndex, false, presenter, gridId);
          shouldPerformGridAction = true;
        }
      }
    } else if (keyboardEvent.shiftKey) {
      if (!AGGridUtils.canCopyRow(dispatcher, gridId))
        return;
      if (AGGridUtils.isEmptyLine(currentRowData)) {
        messagesStore.pushMessageStr("Could not duplicate empty line");
        return;
      }
      await AGGridUtils.asyncCreateRows(currentRowData, api, realRowIndex, true, presenter, gridId);
      shouldPerformGridAction = true;
    }
    if (shouldPerformGridAction) {
      await dispatcher.afterRowInserted(handler, realRowIndex, (keyboardEvent.ctrlKey && keyboardEvent.altKey) || keyboardEvent.shiftKey);
      rows = NamaCommon.getFieldValue(presenter.record.value, gridId);
      api.applyTransaction({update: rows});
    }
  } else if (keyboardEvent.key === 'Delete' && keyboardEvent.ctrlKey) {
    if (!AGGridUtils.canDeleteRow(handler))
      return;
    await AGGridUtils.removeLine(currentRowData, api, handler);
    if (focusedCell && rowIndex === api.getDisplayedRowCount())
      api.setFocusedCell(rowIndex - 1, focusedCell.column);
  } else if (keyboardEvent.key === 'ArrowDown' && focusedCell && AGGridUtils.isFocusedCellEditing(api, focusedCell)) {
    if (focusedCell.rowIndex + 1 < api.getDisplayedRowCount()) {
      api.setFocusedCell(rowIndex + 1, focusedCell.column);
      return;
    }
    if (!AGGridUtils.canAppendRow(presenter, gridId))
      return;
    if (api.isAnyFilterPresent() && !await NaMaConfirmationDialog.okCancelConfirmationDialog(Translator.translateId('areYouSureToAddLinesThatMayNotBeDisplayed'),
      true))
      return;
    AGGridUtils.createRow(currentRowData, api, realRowIndex + 1, presenter, gridId);
    await dispatcher.afterRowInserted(handler, realRowIndex + 1, false);
    let rows = NamaCommon.getFieldValue(presenter.record.value, gridId);
    api.applyTransaction({update: rows});
    if (rowIndex + 1 < api.getDisplayedRowCount())
      api.setFocusedCell(rowIndex + 1, api.getAllDisplayedColumns()[0]);
  } else if (keyboardEvent.key === 'ArrowUp' && focusedCell && focusedCell.rowIndex > 0 && AGGridUtils.isFocusedCellEditing(api, focusedCell)) {
    api.setFocusedCell(focusedCell.rowIndex - 1, focusedCell.column);
  } else if (keyboardEvent.key === 'F9' && focusedCell && focusedCell.column) {
    let colDef = focusedCell?.column?.getColDef();
    if (!colDef)
      return;
    let fieldId: string = colDef.colId ?? '';
    if (colDef.context)
      fieldId = colDef.context.fieldId ?? '';
    if (AGGridUtils.isAgGridBuiltInColumn(fieldId)) {
      let fetchDisplayedColumns = AGGridUtils.fetchDisplayedColumns(event.api);
      const firstDisplayedColumn = fetchDisplayedColumns.shift();
      fieldId = gridId + "." + firstDisplayedColumn;
    }
    await props.presenter.fetchDispatcher().displayTooltip(fieldId, gridId, event?.rowIndex ?? -1, false);
  } else if ((keyboardEvent.key === 'F4' || keyboardEvent.key === 'F6' || keyboardEvent.key === 'F7' || keyboardEvent.key === 'F8') && focusedCell && focusedCell.column) {
    let focusedCellContext: Record<string, any> | null = AGGridUtils.fetchFocusedCellContext(focusedCell, gridApi.value);
    if (focusedCellContext && focusedCellContext.onBrowseClicked)
      focusedCellContext.onBrowseClicked(keyboardEvent);
  } else if ((keyboardEvent.key === 'F' || keyboardEvent.key === 'f') && keyboardEvent.altKey) {
    keyboardEvent.preventDefault();
    emit('toggleFindBar');
  }
}

function onClipboardPaste(params: ProcessDataFromClipboardParams): string[][] {
  const api = gridApi.value
  if (!api)
    return [];
  const data = params.data;
  if (!data)
    return [];
  const emptyLastRow = data[data.length - 1][0] === "" && data[data.length - 1].length === 1;
  if (emptyLastRow)
    data.splice(data.length - 1, 1);
  const lastIndex = api.getDisplayedRowCount() - 1;
  const focusedCell = api.getFocusedCell();
  const focusedIndex = focusedCell!.rowIndex;
  if (focusedIndex + data.length - 1 > lastIndex) {
    const resultLastIndex = focusedIndex + (data.length - 1);
    const numRowsToAdd = resultLastIndex - lastIndex;
    for (let i = 0; i < numRowsToAdd; i++) {
      const index = (lastIndex - focusedIndex + 1) + i;
      const rows = data.slice(index, index + 1)[0];
      const rowObject: any = {};
      for (const [j, rowValue] of rows.entries()) {
        const cellRange = api.getCellRanges()![0];
        const column = cellRange.columns[j]
        rowObject[column?.getColId()] = AGGridUtils.parseClipboardToObjectOrString(rowValue, column)
      }
      AGGridUtils.asyncCreateRows(rowObject, api, (lastIndex + 1) + i, false, presenter, gridId, 1).then(rowNodeTransaction => {
        dispatcher.afterRowInserted(handler, (lastIndex + 1) + i, false).then(_ => {
          if (!rowNodeTransaction)
            return;
          const createdRow = rowNodeTransaction.add[0];
          for (const columnId of Object.keys(rowObject)) {
            const columnDef = api.getColumnDef(columnId)
            const isColumnDisabled = presenter.isColumnDisabled(createdRow.id ?? '', gridId, columnDef?.context.fieldId)
            if (!isColumnDisabled) {
              createdRow.setDataValue(columnId, rowObject[columnId]);
            }
          }
        })
      })
    }
  }

  return params.data;
}

async function onCellDoubleClicked(event: CellDoubleClickedEvent) {
  let colDef = event.colDef;
  let colId = colDef?.colId;
  if (colId !== "ag-Grid-RowNumbersColumn")
    return;
    let fetchDisplayedColumns = AGGridUtils.fetchDisplayedColumns(event.api);
    const firstDisplayedColumn = fetchDisplayedColumns[0]
    if (ObjectChecker.isNotEmptyOrNull(firstDisplayedColumn))
    colId = gridId + "." + firstDisplayedColumn;
  await props.presenter.fetchDispatcher().displayTooltip(colId, gridId, event?.rowIndex ?? -1, false);
}

let draggedRowIndex: number;

function onRowDragEnd(e: RowDragEndEvent) {
  if (dispatcher.isViewOnlyMode()) {
    const originalRows = NamaCommon.getFieldValue(presenter.fetchRecord(), gridId);
    rowData.value = [...originalRows];
    return;
  }
  let overIndex = e.overIndex;
  if (draggedRowIndex == overIndex)
    return;
  const record = presenter.fetchRecord();
  if (!(gridId in record))
    return;
  const gridRecord: Record<any, any>[] = record[gridId];
  let over = gridRecord[overIndex];
  if (overIndex && over.masterRowId) {
    let changedOver = gridRecord.findIndex((r) => r.id === over.masterRowId);
    if (changedOver >= 0)
      overIndex = changedOver;
  }
  let current = gridRecord[draggedRowIndex];
  let children = gridRecord.filter(e => e.masterRowId === current.id)
  const draggedRowRecord = gridRecord.splice(draggedRowIndex, 1);
  if (children.length) {
    draggedRowRecord.push(...children);
    for (let i = gridRecord.length - 1; i >= 0; i--) {
      if (draggedRowRecord.indexOf(gridRecord[i]) >= 0) {
        gridRecord.splice(i, 1);
      }
    }
    gridRecord.splice(Math.min(overIndex, gridRecord.length), 0, ...draggedRowRecord)
  } else {
    gridRecord.splice(overIndex, 0, ...draggedRowRecord)
  }
}

function onRowDragEnter(e: RowDragEnterEvent) {
  draggedRowIndex = e.overIndex
}

function getRowStyle() {
  return (params: RowClassParams) => {
    if (!params.data)
      return;
    const rowBackgroundColor = params.data[StringConstants.NAMA_ROW_BACKGROUND_COLOR] ?? params.data[StringConstants.ROW_BACKGROUND_COLOR];
    if (rowBackgroundColor)
      return {backgroundColor: rowBackgroundColor};
    const namaStyleClass = params.data[StringConstants.NAMA_STYLE_FIELDID];
    const guiSettings = ClientCache.getCustomGUISettings();
    if (namaStyleClass && guiSettings?.gridColoursMap && guiSettings?.gridColoursMap[namaStyleClass])
      return {backgroundColor: "var(--nama-" + namaStyleClass + ")"};
  };
}

function onCellFocused(event: CellFocusedEvent) {
  presenter.updateGridSelectedLine(gridId, event.rowIndex ?? 0);
}

function handleInitialGridState() {
  const storedState = NamaCommon.getStoredValue(gridStateKey, undefined)
  hasStoredState.value = !!storedState;
  gridVisible.value = false;
  setTimeout(() => {
    initialState.value = buildInitialGridState(storedState);
    removeDefaultColDefWidthIfNeeded();
    rowData.value = undefined;
    gridVisible.value = true;
  })
}

function buildInitialGridState(storedState: GridState | undefined): GridState {
  const defaultHiddenColIds = handler.fetchDefaultHiddenColIds();
  if (!storedState)
    return {columnVisibility: {hiddenColIds: defaultHiddenColIds}};
  const knownColIds = NamaCommon.getStoredValue(gridColIdsKey, undefined);
  if (!knownColIds)
    return storedState;
  const knownColIdSet = new Set(knownColIds as string[]);
  const newlyHiddenColIds = defaultHiddenColIds.filter(colId => !knownColIdSet.has(colId));
  if (!newlyHiddenColIds.length)
    return storedState;
  const existingHiddenColIds = storedState.columnVisibility?.hiddenColIds ?? [];
  return {
    ...storedState,
    columnVisibility: {hiddenColIds: [...new Set([...existingHiddenColIds, ...newlyHiddenColIds])]}
  };
}

onMounted(() => {
  handleInitialGridState();
  FieldWidgetUtil.addEditScreenGridSaver(saveEditingCell);
})
onUnmounted(() => {
  FieldWidgetUtil.removeEditScreenGridSaver(saveEditingCell);
  presenter.unregisterGridHandler(gridId);
})

function saveEditingCell() {
  if (gridApi.value)
    gridApi.value.stopEditing();
}

watch(() => fieldToFocusOptions.value.field, async (searchItem) => {
  if (!searchItem)
    return;
  if (searchItem.presenterId && searchItem.presenterId !== presenter.presenterId)
    return;
  if (!toValue(gridApi))
    await new Promise(resolve => setTimeout(resolve, BlockUtils.qExpansionDuration))
  await nextTick();
  if (!gridApi.value || !fieldToFocusOptions.value)
    return;
  await FieldToFocusUtils.focusToAgColumn(presenter, toValue(gridApi), toValue(fieldToFocusOptions), searchItem)
}, {immediate: true});

watch(() => props.presenter.gridEventBus.value[gridId]?.refreshCells, (payload) => {
  if (!payload)
    return;
  const columns = payload.columns?.map(column => AGGridUtils.encodeColId(column));
  gridApi.value?.refreshCells({columns, force: true});
});

watch(() => props.presenter.gridEventBus.value[gridId]?.moveToNextRow, async (__) => {
  setTimeout(async () => {
    const api = gridApi.value;
    if (ObjectChecker.isEmptyOrNull(api))
      return;
    let focusedCellId = props.presenter.gridEventBus.value[gridId].focusedCellId;
    let currentRow = AGGridUtils.fetchFocusedRowData(toValue(gridApi));
    let rows: any[] = NamaCommon.getFieldValue(handler.presenter.fetchRecord(), handler.gridId);
    const currentRowIndex = ObjectChecker.findIndexOf(rows, l => l?.id === currentRow?.id);
    let nextRowIndex = (currentRowIndex ?? -1) + 1;
    const nextRow = api.getDisplayedRowAtIndex(nextRowIndex);
    const colKey = AGGridUtils.encodeColId(gridId + "." + focusedCellId);
    if (ObjectChecker.isNotEmptyOrNull(nextRow)) {
      api.setFocusedCell(nextRowIndex, colKey)
      return;
    }
    await AGGridUtils.appendNewLine(handler);
    api.setFocusedCell(nextRowIndex, colKey)
  }, 0)
});

defineExpose({
  gridApi,
  handler,
  isGridReady
});
</script>

<template>
    <AgGridVue
      v-if="gridVisible"
      v-bind="{...AGGridUtils.getDefaultAgGridOptionsForEditScreen()}"
      ref="gridElement"
      style="width: 100%; font-size: var(--ag-font-size);height: 100%"
      class="listview-grid"
    :getContextMenuItems="agShowFieldInfoContextMenu.getContextMenuItems.value"
    :getMainMenuItems="agShowFieldInfoContextMenu.getMainMenuItems.value"
    rowModelType="clientSide"
    :columnDefs="columnDefs"
    :enableRtl="Translator.isLanguageRTL()"
    :localeText="Translator.fetchAGGridLocale()"
    :rowData="rowData"
    :findSearchValue="findSearchValue"
    :defaultColDef="handler.fetchDefaultColDef()"
    :navigateToNextCell="AGGridUtils.onNavigationToNextCell(presenter, gridId, handler)"
    :tabToNextCell="AGGridUtils.OnTabToNextCell(presenter, gridId, handler)"
    :getRowId="getRowId"
    :getRowStyle="getRowStyle()"
    :processDataFromClipboard="onClipboardPaste"
    :processCellForClipboard="AGGridUtils.processCellForClipboard"
    :processCellFromClipboard="AGGridUtils.processCellFromClipboard"
    :sideBar="sideBarDef"
    :rowSelection="rowSelection"
    :selectionColumnDef="selectionColumnDef"
    :autoSizeStrategy="hasStoredState ? undefined : { type: 'fitCellContents', skipHeader: false }"
    :suppressColumnMoveAnimation="true"
    :suppressMovableColumns="suppressColumnDrag"
    :rowNumbers="true"
    :cellSelection="true"
    :suppressHeaderFocus="true"
    :maintainColumnOrder="true"
    :stopEditingWhenCellsLoseFocus="false"
    :enableFilterHandlers="true"
    :rowDragMultiRow="true"
    :rowDragManaged="true"
    :initialState="initialState"
    @state-updated="onStateUpdated"
    @grid-ready="onGridReady"
    @cell-double-clicked="onCellDoubleClicked"
    @keydown.enter="onEnterKey"
    @cellEditingStopped="onEditingStopped"
    @cellEditingStarted="onEditingStarted"
    @cellKeyDown="onCellKeyDown"
    @cellFocused="onCellFocused"
    @rowDragEnter="onRowDragEnter"
    @rowDragEnd="onRowDragEnd"
  />
</template>

<style>
.listview-grid button.ag-filter-apply-panel-button {
  @apply tw-bg-primary-light tw-rounded tw-p-1 tw-text-namaFontColor-input-dark
}
</style>
